'use client';

import {
  createContext,
  use,
  useRef,
  useState,
  type BaseSyntheticEvent,
  type ReactNode,
} from 'react';
import Link from 'next/link';
import { useForm, type UseFormReturn } from 'react-hook-form';
import type { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  portfolioSizes,
  walkthroughFormSchema,
  walkthroughRoleLabels,
  walkthroughRoles,
} from '@addava/domain/contact';
import { requestWalkthroughAction } from '~/lib/contact/actions';
import { AvaMark } from '~/components/ava-mark';
import { Button } from '~/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';
import { Input } from '~/components/ui/input';
import { NativeSelect, NativeSelectOption } from '~/components/ui/native-select';
import { Textarea } from '~/components/ui/textarea';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '~/components/ui/form';
import s from './home.module.css';

type WalkthroughValues = z.output<typeof walkthroughFormSchema>;
const OpenWalkthrough = createContext<(() => void) | null>(null);

const blank = {
  role: 'pm' as const,
  name: '',
  email: '',
  phone: '',
  company: '',
  doors: undefined,
  where: '',
  what: '',
  website: '',
};

/** One walkthrough dialog for the page; every "Add Ava" button opens it. */
export function WalkthroughProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  // The dialog unmounts its content when closed, so the form, its submission id and
  // the result live here: a draft survives an accidental close, and a request that
  // finishes after the visitor closed the dialog still shows its outcome on reopen.
  const form = useForm({ resolver: zodResolver(walkthroughFormSchema), defaultValues: blank });
  const submission = useRef<{ payload: string; id: string } | null>(null);
  const [sent, setSent] = useState<{ firstName: string; seen: boolean } | null>(null);
  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root');
    const payload = JSON.stringify(values);
    if (submission.current?.payload !== payload)
      submission.current = { payload, id: crypto.randomUUID() };
    try {
      const result = await requestWalkthroughAction({
        ...values,
        kind: 'walkthrough',
        submissionId: submission.current.id,
      });
      if (result?.data?.success)
        setSent({ firstName: values.name.split(/\s+/)[0] || 'there', seen: false });
      else
        form.setError('root', {
          message:
            result?.serverError ??
            'We could not send your request. Check your details and try again.',
        });
    } catch {
      form.setError('root', {
        message: 'We could not send your request. Check your connection and try again.',
      });
    }
  });
  return (
    <OpenWalkthrough
      value={() => {
        // A fresh form once the visitor has seen the thanks for the last request.
        if (sent?.seen) {
          setSent(null);
          submission.current = null;
          form.reset(blank);
        }
        setOpen(true);
      }}
    >
      {children}
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next && sent && open) setSent({ ...sent, seen: true });
          setOpen(next);
        }}
      >
        <DialogContent
          overlayClassName="bg-ink/55 supports-backdrop-filter:backdrop-blur-[4px]"
          className="max-h-[calc(100dvh-3rem)] flex flex-col gap-0 overflow-hidden rounded-[20px] p-0 text-base shadow-[0_40px_80px_-24px_rgb(0_0_0/45%)] sm:max-w-[640px]"
        >
          {sent === null ? (
            <>
              <DialogHeader className="shrink-0 px-7 pt-7 pb-5 pr-12">
                <DialogTitle className="text-2xl leading-tight tracking-tight">
                  Add Ava to your team
                </DialogTitle>
                <DialogDescription className="text-[15px] leading-relaxed">
                  Tell us about your portfolio. We’ll set up a walkthrough on your own buildings.
                </DialogDescription>
              </DialogHeader>
              <WalkthroughForm form={form} onSubmit={submit} />
            </>
          ) : (
            <div
              role="status"
              className="grid justify-items-start gap-4 p-7"
              data-test="walkthrough-success"
            >
              <AvaMark state="done" size={48} play />
              <DialogTitle className="text-2xl leading-tight tracking-tight">
                Thanks, {sent.firstName}.
              </DialogTitle>
              <DialogDescription className="text-base leading-relaxed">
                Your request has been sent to the AddAva team. We’ll email you to find a time for a
                walkthrough on your buildings.
              </DialogDescription>
              <Button
                size="marketing"
                className="btn-primary px-5"
                onClick={() => {
                  setSent({ ...sent, seen: true });
                  setOpen(false);
                }}
              >
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </OpenWalkthrough>
  );
}

/** Shares the page's walkthrough dialog when there is one, and brings its own otherwise. */
export function WalkthroughScope({ children }: { children: ReactNode }) {
  return use(OpenWalkthrough) ? children : <WalkthroughProvider>{children}</WalkthroughProvider>;
}

export function WalkthroughButton({
  className,
  children,
  test,
}: {
  className?: string;
  children: ReactNode;
  test?: string;
}) {
  const open = use(OpenWalkthrough);
  if (!open) throw new Error('WalkthroughButton requires WalkthroughProvider');
  return (
    <button type="button" className={className} onClick={open} data-test={test}>
      {children}
    </button>
  );
}

function WalkthroughForm({
  form,
  onSubmit,
}: {
  form: UseFormReturn<z.input<typeof walkthroughFormSchema>, unknown, WalkthroughValues>;
  onSubmit: (event?: BaseSyntheticEvent) => Promise<void>;
}) {
  const pending = form.formState.isSubmitting;
  return (
    <Form {...form}>
      <form
        className="flex min-h-0 flex-col text-sm"
        data-test="walkthrough-form"
        noValidate
        aria-busy={pending}
        onSubmit={onSubmit}
      >
        <div className="min-h-0 overflow-y-auto px-7 py-2">
          <fieldset disabled={pending} className="space-y-4">
            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <fieldset>
                  <legend className="mb-2 text-sm font-semibold">Which best describes you?</legend>
                  <div className={s.roles}>
                    {walkthroughRoles.map((role) => (
                      <label key={role}>
                        <input
                          type="radio"
                          name={field.name}
                          value={role}
                          checked={field.value === role}
                          onChange={() => field.onChange(role)}
                          onBlur={field.onBlur}
                        />
                        <span>{walkthroughRoleLabels[role]}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>
              )}
            />
            <div className="grid items-start gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Your name</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        autoComplete="name"
                        maxLength={200}
                        required
                        className="h-12"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Work email</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="email"
                        autoComplete="email"
                        maxLength={254}
                        required
                        className="h-12"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="grid items-start gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="tel"
                        inputMode="tel"
                        autoComplete="tel"
                        maxLength={40}
                        placeholder="(415) 555-0134"
                        required
                        className="h-12"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="company"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Company</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        autoComplete="organization"
                        maxLength={200}
                        required
                        className="h-12"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="grid items-start gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="doors"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Doors you manage</FormLabel>
                    <FormControl>
                      <NativeSelect
                        {...field}
                        value={field.value ?? ''}
                        required
                        className="w-full"
                      >
                        <NativeSelectOption value="" disabled>
                          Select
                        </NativeSelectOption>
                        {portfolioSizes.map((size) => (
                          <NativeSelectOption key={size} value={size}>
                            {size}
                          </NativeSelectOption>
                        ))}
                      </NativeSelect>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="where"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Where are your properties?</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        maxLength={500}
                        placeholder="e.g. San Francisco, Oakland"
                        className="h-12"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="what"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    What keeps landing on your team?{' '}
                    <span className="font-normal text-muted-foreground">(optional)</span>
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      rows={3}
                      maxLength={5000}
                      placeholder="Notices, permits, vendor insurance, inspections…"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div hidden aria-hidden="true">
              <label htmlFor="walkthrough-website">Leave this field empty</label>
              <input
                id="walkthrough-website"
                tabIndex={-1}
                autoComplete="off"
                {...form.register('website')}
              />
            </div>
          </fieldset>
        </div>
        <div className="shrink-0 space-y-4 border-t px-7 py-5">
          <p className="text-xs leading-relaxed text-muted-foreground">
            We’ll use your details to set up the walkthrough. No newsletter. By sharing your phone
            number, you agree we can call or text you about your walkthrough. Message and data rates
            may apply. Reply STOP to opt out.{' '}
            <Link href="/privacy-policy" className="underline underline-offset-4">
              Privacy policy
            </Link>
          </p>
          <Button
            type="submit"
            disabled={pending}
            size="marketing"
            className="btn-primary px-5"
            data-test="walkthrough-submit"
          >
            {pending ? 'Sending…' : 'Show me what Ava can do'}
          </Button>
        </div>
        {form.formState.errors.root && (
          <p role="alert" className="text-sm text-destructive">
            {form.formState.errors.root.message}
          </p>
        )}
      </form>
    </Form>
  );
}
